import { ListChecks } from "lucide-react";
import type { TaskWithFlags } from "@desk/contracts";
import type { BoardCardProps, BoardColumnModel, BoardDnd, BoardProps } from "../types";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { DeadlineLabel, SnoozeBadge, SyncBadge } from "@/components/task-bits";
import { cn } from "@/lib/utils";

function Card({ task, card, dnd }: { task: TaskWithFlags; card: BoardCardProps; dnd: BoardDnd }) {
  const d = dnd.useCard(task.id);
  const closed = task.status === "done" || task.status === "cancelled";
  const done = task.checklist.filter((c) => c.done).length;
  return (
    <div
      ref={d.setNodeRef}
      style={d.style}
      {...d.attributes}
      {...d.listeners}
      data-task-id={task.id}
      onClick={card.onOpen}
      className={cn(
        "group/card relative cursor-grab rounded-lg border border-border/80 bg-card px-3 pb-2.5 pt-2 text-left transition-[border-color,box-shadow] active:cursor-grabbing",
        "hover:border-foreground/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        card.selected && "border-foreground/35",
        d.isDragging && "cursor-grabbing shadow-card",
      )}
    >
      <div className="flex h-6 items-center gap-2 text-[12px] text-subtle">
        {task.readableId !== "DSK-0" && <span className="tabular">{task.readableId}</span>}
        <span className="ml-auto inline-flex items-center transition-opacity group-hover/card:opacity-0 group-has-[[aria-haspopup=menu][aria-expanded=true]]/card:opacity-0">
          {task.priority !== "none" && <PriorityIcon priority={task.priority} className="size-3.5" />}
        </span>
        <span
          className="absolute right-1.5 top-1.5 rounded-md bg-card opacity-0 transition-opacity focus-within:opacity-100 group-hover/card:opacity-100 has-[[aria-haspopup=menu][aria-expanded=true]]:opacity-100"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {card.menu}
        </span>
      </div>
      <p className={cn("clamp-2 break-words text-[13.5px] leading-[19px] text-foreground", closed && "text-muted-foreground line-through decoration-foreground/25")}>
        {task.title}
      </p>
      {(card.location || (task.deadline && !closed) || task.checklist.length > 0 || card.sync || task.flags.isSnoozed) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-muted-foreground">
          {task.deadline && !closed && <DeadlineLabel deadline={task.deadline} today={card.today} />}
          <SnoozeBadge task={task} timezone={card.timezone} />
          <SyncBadge mark={card.sync} />
          {task.checklist.length > 0 && (
            <span className="inline-flex items-center gap-1 tabular text-subtle" aria-label={`Checklist ${done} of ${task.checklist.length}`}>
              <ListChecks className="size-3" aria-hidden />
              {done}/{task.checklist.length}
            </span>
          )}
          {card.location && <span className="min-w-0 max-w-[180px] truncate text-subtle">{card.location}</span>}
        </div>
      )}
    </div>
  );
}

function Column({ col, card, dnd }: { col: BoardColumnModel; card: BoardProps["card"]; dnd: BoardDnd }) {
  const drop = dnd.useColumn(col.status);
  return (
    <section
      ref={drop.setNodeRef}
      aria-label={`${col.label}, ${col.tasks.length} tasks`}
      className={cn(
        "flex w-[276px] shrink-0 flex-col rounded-xl p-1.5 transition-colors duration-150",
        drop.isOver ? "bg-selected" : "bg-foreground/[0.035] dark:bg-white/[0.03]",
      )}
    >
      <header className="flex h-8 items-center gap-2 px-1.5">
        <StatusIcon status={col.status} className="size-3.5" />
        <h2 className="font-sans text-[13px] font-semibold tracking-normal text-foreground/85">{col.label}</h2>
        <span className="text-[12px] text-subtle tabular">{col.tasks.length}</span>
      </header>
      <div className="flex min-h-20 flex-col gap-1.5">
        {col.tasks.map((t) => (
          <Card key={t.id} task={t} card={card(t)} dnd={dnd} />
        ))}
      </div>
    </section>
  );
}

export function FocusBoard({ columns, card, dnd, empty }: BoardProps) {
  const total = columns.reduce((n, c) => n + c.tasks.length, 0);
  return (
    <div className="-mx-2" data-wide>
      {total === 0 && empty}
      <div className="flex gap-2.5 overflow-x-auto px-2 pb-6">
        {columns.map((c) => (
          <Column key={c.status} col={c} card={card} dnd={dnd} />
        ))}
      </div>
    </div>
  );
}
